import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TablePagination,
  CircularProgress,
  Alert,
} from "@mui/material";
import { FileDown, X } from "lucide-react";
import { useState, useEffect } from "react";

const getEfficiencyLabel = (difference, estimatedHours) => {
  const percentDifference = (difference / estimatedHours) * 100;
  if (difference <= 0) return { label: "Within Estimate", color: "#4CAF50" }; // Green
  if (percentDifference <= 20)
    return { label: "Slightly Over", color: "#FFC107" }; // Yellow
  return { label: "Significantly Over (>20%)", color: "#F44336" }; // Red
};

export default function ScaffoldBuildTimeTracking({ onClose }) {
  const [scaffoldData, setScaffoldData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchScaffoldData();
  }, []);

  const fetchScaffoldData = async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/scaffold-data");
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setScaffoldData(data);
      setError(null);
    } catch (err) {
      setError("Failed to load scaffold data. Please try again later.");

      setScaffoldData([
        {
          id: "S-001",
          projectName: "Project Alpha",
          taskType: "Assembly",
          estimatedHours: 24,
          actualHours: 22,
          difference: -2,
        },
        {
          id: "S-002",
          projectName: "Project Alpha",
          taskType: "Modification",
          estimatedHours: 8,
          actualHours: 10,
          difference: 2,
        },
        {
          id: "S-003",
          projectName: "Project Beta",
          taskType: "Assembly",
          estimatedHours: 32,
          actualHours: 40,
          difference: 8,
        },
        {
          id: "S-004",
          projectName: "Project Gamma",
          taskType: "Dismantling",
          estimatedHours: 16,
          actualHours: 15,
          difference: -1,
        },
        {
          id: "S-005",
          projectName: "Project Delta",
          taskType: "Assembly",
          estimatedHours: 48,
          actualHours: 72,
          difference: 24,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
    setPage(0);
  };

  const handleFilter = (event) => {
    setFilter(event.target.value);
    setPage(0);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredData = scaffoldData
    .filter(({ id, projectName, taskType }) =>
      [id, projectName?.toLowerCase(), taskType?.toLowerCase()].some((field) =>
        field?.includes(searchTerm)
      )
    )
    .filter((item) => (filter ? item.taskType === filter : true));

  const exportToCSV = () => {
    const headers =
      "Scaffold ID,Project Name,Task Type,Estimated Hours,Actual Hours,Difference,Efficiency";
    const csvRows = scaffoldData.map(
      ({
        id,
        projectName,
        taskType,
        estimatedHours,
        actualHours,
        difference,
      }) => {
        const efficiency = getEfficiencyLabel(difference, estimatedHours).label;
        return `${id},${projectName},${taskType},${estimatedHours},${actualHours},${difference},${efficiency}`;
      }
    );
    const csvContent = [headers, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "scaffold_build_time_tracking.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const taskTypes = [...new Set(scaffoldData.map((item) => item.taskType))];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card
        sx={{ width: "100%", padding: 2, minHeight: "90vh", overflow: "auto" }}
      >
        <CardHeader
          title="Scaffold Build Time Tracking"
          action={
            <div className="flex space-x-2">
              <Button
                variant="outlined"
                onClick={exportToCSV}
                startIcon={<FileDown size={18} />}
                disabled={loading || scaffoldData.length === 0}
              >
                Export CSV
              </Button>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-6 w-6" />
              </Button>
            </div>
          }
        />

        <CardContent>
          {/* {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )} */}

          <input
            type="text"
            placeholder="Search by ID, Project Name, or Task Type"
            onChange={handleSearch}
            disabled={loading}
            style={{
              width: "100%",
              height: "3rem",
              padding: "8px 12px",
              fontSize: "16px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              outline: "none",
              transition: "border-color 0.2s ease-in-out",
              marginBottom: "1rem",
              opacity: loading ? 0.7 : 1,
            }}
            onFocus={(e) => (e.target.style.borderColor = "#1976d2")}
            onBlur={(e) => (e.target.style.borderColor = "#ccc")}
          />

          <FormControl
            fullWidth
            sx={{ mb: 2 }}
            variant="outlined"
            disabled={loading}
          >
            <InputLabel id="task-type-label">Filter by Task Type</InputLabel>
            <Select
              labelId="task-type-label"
              value={filter}
              onChange={handleFilter}
              label="Filter by Task Type"
            >
              <MenuItem value="">All</MenuItem>
              {taskTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "2rem",
              }}
            >
              <CircularProgress />
            </div>
          ) : scaffoldData.length === 0 ? (
            <Alert severity="info">No scaffold data available.</Alert>
          ) : filteredData.length === 0 ? (
            <Alert severity="info">
              No results found. Try adjusting your search or filter.
            </Alert>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <b>Scaffold ID</b>
                      </TableCell>
                      <TableCell>
                        <b>Project Name</b>
                      </TableCell>
                      <TableCell>
                        <b>Task Type</b>
                      </TableCell>
                      <TableCell align="right">
                        <b>Estimated Hours</b>
                      </TableCell>
                      <TableCell align="right">
                        <b>Actual Hours</b>
                      </TableCell>
                      <TableCell align="right">
                        <b>Difference</b>
                      </TableCell>
                      <TableCell align="right">
                        <b>Efficiency</b>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map(
                        ({
                          id,
                          projectName,
                          taskType,
                          estimatedHours,
                          actualHours,
                          difference,
                        }) => {
                          const { label, color } = getEfficiencyLabel(
                            difference,
                            estimatedHours
                          );
                          return (
                            <TableRow
                              key={id}
                              sx={{
                                backgroundColor: color + "20",
                                "&:hover": { backgroundColor: color + "50" },
                              }}
                            >
                              <TableCell>{id}</TableCell>
                              <TableCell>{projectName}</TableCell>
                              <TableCell>{taskType}</TableCell>
                              <TableCell align="right">
                                {estimatedHours}
                              </TableCell>
                              <TableCell align="right">{actualHours}</TableCell>
                              <TableCell align="right">{difference}</TableCell>
                              <TableCell align="right">
                                <Typography sx={{ color }}>{label}</Typography>
                              </TableCell>
                            </TableRow>
                          );
                        }
                      )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
